import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';

import { ActiveEmployerCompanyService } from '../../services/active-employer-company/active-employer-company.service';
import { NativeTeamContextService } from '../../services/native-team-context/native-team-context.service';
import { PayrollAccessService } from '../../services/payroll-access/payroll-access.service';
import { SessionService } from '../../services/session/session.service';

interface ScopedSuperadminCompanyRequest {
  requested: boolean;
  companyId: string;
  teamId: string;
}

@Injectable({
  providedIn: 'root',
})
export class PayrollWorkspaceGuard implements CanActivate, CanActivateChild {
  constructor(
    private activeEmployerCompanyService: ActiveEmployerCompanyService,
    private nativeTeamContextService: NativeTeamContextService,
    private payrollAccessService: PayrollAccessService,
    private sessionService: SessionService,
    private router: Router,
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    return this.evaluate(state.url);
  }

  canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    return this.evaluate(state.url);
  }

  private async evaluate(url: string): Promise<boolean | UrlTree> {
    const access = await this.payrollAccessService.refresh();
    const scopedSuperadminRequest = this.getScopedSuperadminCompanyRequest(url);
    if (access.workspaceContext === 'fullPayrollAdmin' && scopedSuperadminRequest.requested) {
      return this.evaluateScopedSuperadminCompanyWorkspace(
        access.canShowPayrollAdminNav,
        scopedSuperadminRequest,
      );
    }

    const scopedSuperadminWorkspaceWasActive =
      this.activeEmployerCompanyService.state.workspaceContext === 'superadminCompany';
    this.activeEmployerCompanyService.clearScopedSuperadminCompany();
    if (scopedSuperadminWorkspaceWasActive) {
      this.nativeTeamContextService.clear();
    }

    if (access.workspaceContext !== 'nativeCompanyManager') {
      return true;
    }

    const companyState = await this.activeEmployerCompanyService.initialize();
    if (!this.activeEmployerCompanyService.isSupportedSingleCompanyWorkspaceActive(companyState)) {
      return this.router.parseUrl('/');
    }
    await this.nativeTeamContextService.initialize();

    const payrollChildPath = this.getPayrollChildPath(url);
    if (!payrollChildPath || payrollChildPath === 'teams') {
      return payrollChildPath === 'teams'
        ? true
        : this.router.parseUrl('/payrolladmin/teams');
    }

    if (
      (payrollChildPath === 'benefits' || payrollChildPath === 'products')
      && companyState.hasBenefits
    ) {
      return true;
    }

    if (
      payrollChildPath === 'expense-approvals'
      && access.canAccessPayrollExpenseApprovals
    ) {
      return true;
    }

    return this.router.parseUrl('/payrolladmin/teams');
  }

  private async evaluateScopedSuperadminCompanyWorkspace(
    nativeAccessFlagEnabled: boolean,
    request: ScopedSuperadminCompanyRequest,
  ): Promise<boolean | UrlTree> {
    if (
      !nativeAccessFlagEnabled
      || !this.sessionService.isExactSuperAdmin()
      || !request.companyId
      || !request.teamId
    ) {
      return this.router.parseUrl('/');
    }

    const companyState = await this.activeEmployerCompanyService
      .initializeScopedSuperadminCompany(request.companyId);
    if (
      !this.activeEmployerCompanyService.isScopedSuperadminCompanyWorkspaceActive(companyState)
      || `${companyState.activeCompany?.id || ''}` !== request.companyId
    ) {
      return this.router.parseUrl('/');
    }

    const teamState = await this.nativeTeamContextService.initialize();
    const requestedTeam = teamState.active
      && teamState.companyId === request.companyId
      ? teamState.teams.find((team) => `${team?.id || ''}` === request.teamId)
      : undefined;
    if (!requestedTeam) {
      return this.router.parseUrl('/');
    }

    const selectedTeam = await this.nativeTeamContextService.selectTeam(request.teamId);
    return `${selectedTeam?.id || ''}` === request.teamId
      ? true
      : this.router.parseUrl('/');
  }

  private getScopedSuperadminCompanyRequest(url: string): ScopedSuperadminCompanyRequest {
    const [pathAndQuery] = `${url || ''}`.split('#');
    const [path, query = ''] = pathAndQuery.split('?');
    const params = new URLSearchParams(query);
    const segments = path.split('/').filter(Boolean);
    const payrollAdminIndex = segments.indexOf('payrolladmin');
    const payrollChildPath = payrollAdminIndex >= 0 ? `${segments[payrollAdminIndex + 1] || ''}` : '';
    const requested = params.get('workspace') === 'company';

    return {
      requested,
      companyId: requested && payrollChildPath === 'teams'
        ? `${params.get('companyId') || ''}`.trim()
        : '',
      teamId: requested && payrollChildPath === 'teams'
        ? `${params.get('teamId') || ''}`.trim()
        : '',
    };
  }

  private getPayrollChildPath(url: string): string {
    const path = `${url || ''}`.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    const payrollAdminIndex = segments.indexOf('payrolladmin');
    return payrollAdminIndex >= 0 ? `${segments[payrollAdminIndex + 1] || ''}` : '';
  }
}
